import React, { useState, useEffect, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Layout from "../../Layout/Layout";
import "../../css/assessment.css";
import axios from "axios";
import moment from "moment";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux"; // keep for alert slice
import useUserStore from "../../zustand-stores/UserStore";
import { ChatReceive, ChatSend } from "../Chat/ChatBody";
import { MessageBox } from "../Chat/ChatInput";
import { API_BASE } from "../../Utils/constant";
import { store } from "../../store";
import { resetAlert, setAlert } from "../../store/features/alertSlice";
import { usePromptWithModal } from "../../hooks/usePromptWithModal";
import { AUTH_TOKEN } from "../../constants/localStorageKeys";
import { retrieve } from "../../helpers/StorageHelper";
import { useGetDoneAchievedTasks } from "../../Controllers/TaskManagementController";
import { completeJournalHabitForDate } from "../../Controllers/DashboardController";
import { getUserTimezone } from "../../helpers/TimezoneHelper";

export default function ReflectionJournal() {
  const { reflectionType } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Add user_reflection_id state similar to Journal component
  const [userReflectionId, setUserReflectionId] = useState(() => {
    return location.state?.id || null;
  });
  const [editingScoreIndex, setEditingScoreIndex] = useState(null);
  const { user: userData } = useUserStore();
  const [isThinkingLoading, setIsThinkingLoading] = useState(false);

  const [journalData, setJournalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [completedMessage, setCompletedMessage] = useState("");
  const [journalingBegun, setJournalingBegun] = useState(false);
  const [showYesNoModal, setShowYesNoModal] = useState(false);
  const [currentYesNoQuestion, setCurrentYesNoQuestion] = useState(null);
  const [showScoreSlider, setShowScoreSlider] = useState(true);

  const [editingMessage, setEditingMessage] = useState(null);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);

  // Per-question slider values: { [questionIndex]: value }
  const [scoreValues, setScoreValues] = useState({});
  const [selectedTasks, setSelectedTasks] = useState([]); // For win stack questions

  // Slider state and refs (copied from AssessmentView)
  const [sliderValue, setSliderValue] = useState(0); // Deprecated, kept for legacy, use scoreValues instead
  const sliderRef = useRef(null);
  const labelsContainerRef = useRef(null);
  const [isCompleted, setIsCompleted] = useState(false);

  const { alertState } = useSelector((state) => state.alert);
  const {
    showModal: showConfirmModal,
    proceed,
    cancel,
  } = usePromptWithModal(alertState, "Do you really want to leave this page?");

  // Hook for fetching done/achieved tasks
  const { data: doneAchievedTasks, isLoading: doneAchievedLoading } =
    useGetDoneAchievedTasks();
  // Build decoupled task lists object for win-stack rendering
  const buildTaskLists = () => {
    const tasks = doneAchievedTasks || {};

    const toTitles = (arr) =>
      Array.isArray(arr)
        ? arr
            .map((t) => {
              // items may be objects with title or text fields
              if (typeof t === "string") return t;
              if (t && typeof t.title === "string") return t.title;
              if (t && typeof t.text === "string") return t.text;
              return null;
            })
            .filter(Boolean)
        : [];

    const positives = [
      ...toTitles(tasks.achieved_tasks),
      ...toTitles(tasks.done_tasks),
    ];
    const negatives = toTitles(tasks.not_done);

    const result = {};
    if (positives.length > 0) {
      result.taskList1 = { showSuccessTick: true, items: positives };
    }
    if (negatives.length > 0) {
      result.taskList2 = { showSuccessTick: false, items: negatives };
    }
    const buildTaskLists = Object.keys(result).length ? result : null;
  };

  const chatContainerRef = useRef(null);
  const previousReflectionTypeRef = useRef(null);
  const amHabitCompletionTriggeredRef = useRef(false);

  /** localStorage draft scope: reflection type (route) + session + question (main vs follow-up) */
  const reflectionComposerDraftScopeId = useMemo(() => {
    if (!reflectionType || !currentQuestion?.id || editingMessage) {
      return undefined;
    }
    const session = userReflectionId ?? "new";
    const qKind = currentQuestion.isFollowUp ? "followup" : "main";
    return `${reflectionType}:ur:${session}:q:${currentQuestion.id}:${qKind}`;
  }, [
    reflectionType,
    userReflectionId,
    currentQuestion?.id,
    currentQuestion?.isFollowUp,
    editingMessage,
  ]);

  // Slider helper functions (copied from AssessmentView)
  const createLabels = (slider, labelsContainer) => {
    if (!slider || !labelsContainer) return;

    const sliderRect = slider.getBoundingClientRect();
    const min = parseInt(slider.min);
    const max = parseInt(slider.max);

    labelsContainer.innerHTML = "";

    for (let i = min; i <= max; i++) {
      const label = document.createElement("span");
      label.textContent = i;
      label.style.position = "absolute";
      const percent = (i - min) / (max - min);
      const left = percent * sliderRect.width;
      const right = (1 - percent) * sliderRect.width;
      if (i > 6) {
        label.style.right = `${right}px`;
      } else {
        label.style.left = `${left}px`;
      }
      labelsContainer.appendChild(label);
    }
  };

  const updateSliderBackground = (slider) => {
    if (!slider) return;
    const min = slider.min;
    const max = slider.max;
    const val = slider.value;
    slider.style.setProperty("--value", (val - min) / (max - min));
  };

  // Create API config helper function similar to Journal component
  const createApiConfig = (token, includeUserReflectionId = true) => {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        time_zone: getUserTimezone(),
      },
    };

    if (includeUserReflectionId && userReflectionId) {
      config.params = {
        user_reflection_id: userReflectionId,
      };
    }

    return config;
  };

  // handleJournalCompletion
  const handleJournalCompletion = () => {
    const completeAmJournalHabit = async () => {
      if (reflectionType !== "am") return;
      if (amHabitCompletionTriggeredRef.current) return;

      amHabitCompletionTriggeredRef.current = true;

      try {
        const today = moment().format("YYYY-MM-DD");
        await completeJournalHabitForDate(today);

        queryClient.invalidateQueries({ queryKey: ["habitTracker"] });
        queryClient.invalidateQueries({ queryKey: ["dashboard", "habits"] });
      } catch (err) {
        // Keep journal completion UX intact even if habit API fails.
        amHabitCompletionTriggeredRef.current = false;
        console.error("Failed to auto-complete AM journal habit:", err);
      }
    };

    completeAmJournalHabit();

    setIsThinkingLoading(true);
    setAllQuestionsAnswered(true);
    setCurrentQuestion(null);

    // Invalidate power stats and streaks (GET /v3/streak via useGetUserStreaks)
    queryClient.invalidateQueries({ queryKey: ["powerStats"] });
    queryClient.invalidateQueries({
      queryKey: ["dashboard", "userStreaks"],
      refetchType: "all",
    });

    setMessages((prev) => [
      ...prev,
      {
        type: "send",
        question: `Thank you for completing ${reflectionType} journal entry.`,
        time: moment().format("h:mm A"),
        botImage: "/icons/journal-brave.svg",
      },
    ]);

    setTimeout(() => {
      setIsThinkingLoading(false);
    }, 1000);

    setTimeout(() => {
      // Navigate to reflection library first
      navigate("/reflection-library");

      // Invalidate journal status after navigation (non-blocking)
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["dashboard", "journalStatus", "am"],
        });
        queryClient.invalidateQueries({
          queryKey: ["dashboard", "journalStatus", "pm"],
        });
      }, 100);
    }, 2000);
  };

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages, isThinkingLoading]);

  // Slider and labels setup (copied from AssessmentView)
  useEffect(() => {
    const slider = sliderRef.current;
    const labelsContainer = labelsContainerRef.current;

    if (slider && labelsContainer && showScoreSlider) {
      createLabels(slider, labelsContainer);
      updateSliderBackground(slider);

      const resizeObserver = new ResizeObserver(() => {
        createLabels(slider, labelsContainer);
      });
      resizeObserver.observe(slider);

      return () => resizeObserver.disconnect();
    }
  }, [currentQuestion, showScoreSlider]);

  // Update slider value when sliderValue changes
  useEffect(() => {
    const slider = sliderRef.current;
    if (slider && slider.value !== sliderValue) {
      slider.value = sliderValue;
      updateSliderBackground(slider);
    }
  }, [sliderValue]);

  // helper to extract numeric score from answer object
  const getScoreValue = (ans) => {
    if (!ans) return null;
    if (typeof ans.score === "number") return ans.score;
    const n = parseInt(ans.text);
    return Number.isFinite(n) ? n : null;
  };

  const initializeChat = (journal) => {
    setIsThinkingLoading(true);
    const chatMessages = [];
    let nextQuestion = null;
    let lastAnswerTime = null;
    let foundUnanswered = false;

    // Ensure "title" keyword question appears first
    const norm = (s) => (s || "").trim().toLowerCase();
    const orderedMainQuestions = [...journal.main_questions].sort((a, b) => {
      const aIsTitle = norm(a.keywords) === "title";
      const bIsTitle = norm(b.keywords) === "title";
      if (aIsTitle && !bIsTitle) return -1;
      if (!aIsTitle && bIsTitle) return 1;
      return 0; // keep relative order for others
    });

    // Process each main question in order
    for (const mainQuestion of orderedMainQuestions) {
      // If we've already found an unanswered question, break out of main loop
      if (foundUnanswered) {
        break;
      }

      // Check if this main question is answered
      if (mainQuestion.answered && mainQuestion.answer) {
        // Add the main question to chat
        const questionTime = moment(mainQuestion.answer.created_at);
        chatMessages.push({
          type: "send",
          question: mainQuestion.question,
          time: questionTime.format("h:mm A"),
          botImage: "/icons/journal-brave.svg",
          answeredValue:
            mainQuestion.keywords === "score"
              ? getScoreValue(mainQuestion.answer)
              : null,
          // Attach task lists for win-stack questions
          ...(norm(mainQuestion.keywords) === "win stack" ||
          norm(mainQuestion.keywords) === "winstack"
            ? { showTaskList: true, taskLists: buildTaskLists() }
            : {}),
        });

        lastAnswerTime = moment(mainQuestion.answer.created_at);
        chatMessages.push({
          type: "receive",
          response: mainQuestion.answer.text,
          time: lastAnswerTime.format("h:mm A"),
          responder: userData?.full_name || "User",
          userImage: userData?.image || "/icons/toni.svg",
          answerId: mainQuestion.answer.id, // Add this
          questionType:
            mainQuestion.keywords === "score"
              ? "score"
              : mainQuestion?.questionType, // Add this
        });

        // Process follow-up questions if they exist
        if (
          mainQuestion.follow_up_questions &&
          mainQuestion.follow_up_questions.length > 0
        ) {
          for (const followUpQuestion of mainQuestion.follow_up_questions) {
            if (followUpQuestion.answered && followUpQuestion.answer) {
              const followUpTime = moment(followUpQuestion.answer.created_at);
              chatMessages.push({
                type: "send",
                question: followUpQuestion.question,
                time: followUpTime.format("h:mm A"),
                botImage: "/icons/journal-brave.svg",
                answeredValue:
                  followUpQuestion.keywords === "score"
                    ? getScoreValue(followUpQuestion.answer)
                    : null,
                ...(norm(followUpQuestion.keywords) === "win stack" ||
                norm(followUpQuestion.keywords) === "winstack"
                  ? { showTaskList: true, taskLists: buildTaskLists() }
                  : {}),
              });

              lastAnswerTime = moment(followUpQuestion.answer.created_at);
              chatMessages.push({
                type: "receive",
                response: followUpQuestion.answer.text,
                time: lastAnswerTime.format("h:mm A"),
                responder: userData?.full_name || "User",
                userImage: userData?.image || "/icons/toni.svg",
                answerId: followUpQuestion.answer.id, // Use follow-up's answer ID here
                questionType:
                  followUpQuestion?.keywords === "score"
                    ? "score"
                    : followUpQuestion?.questionType, // Add this
              });
            } else if (!foundUnanswered) {
              // Found an unanswered follow-up question, set next and break loops
              nextQuestion = {
                ...followUpQuestion,
                isFollowUp: true,
                mainQuestionId: mainQuestion.id,
              };
              foundUnanswered = true;
              break; // break follow-up loop
            }
          }
        }
      } else if (!foundUnanswered) {
        // Found an unanswered main question, set next and break
        nextQuestion = { ...mainQuestion, isFollowUp: false };
        foundUnanswered = true;
      }
    }

    // Check if all questions are answered
    const allMainAnswered = orderedMainQuestions.every((q) => q.answered);
    const allFollowUpAnswered = orderedMainQuestions.every(
      (mainQ) =>
        !mainQ.follow_up_questions ||
        mainQ.follow_up_questions.every((followUp) => followUp.answered),
    );
    const allAnswered = allMainAnswered && allFollowUpAnswered;

    setAllQuestionsAnswered(allAnswered);

    // Add the next unanswered question to chat (if any)
    if (nextQuestion && !allAnswered) {
      const questionTime = lastAnswerTime || moment();
      chatMessages.push({
        type: "send",
        question: nextQuestion.question,
        time: questionTime.format("h:mm A"),
        botImage: "/icons/journal-brave.svg",
        ...(norm(nextQuestion.keywords) === "win stack" ||
        norm(nextQuestion.keywords) === "winstack"
          ? { showTaskList: true, taskLists: buildTaskLists() }
          : {}),
        // ensure current score question displays the slider
        answeredValue: nextQuestion.keywords === "score" ? null : null,
      });

      if (nextQuestion.question_type === "yes_no") {
        setCurrentYesNoQuestion(nextQuestion);
        setShowYesNoModal(true);
        setTimeout(() => {
          setIsThinkingLoading(false);
        }, 2000);
      } else if (nextQuestion.keywords === "score") {
        setShowScoreSlider(true);
        setSliderValue(0); // Initialize slider with default value
        setScoreValues((prev) => ({ ...prev, [chatMessages.length]: 0 }));
        // Show thinking loader for ~2 seconds, then reveal slider and initialize visuals
        setTimeout(() => {
          setIsThinkingLoading(false);
          setTimeout(() => {
            const slider = sliderRef.current;
            const labelsContainer = labelsContainerRef.current;
            if (slider && labelsContainer) {
              createLabels(slider, labelsContainer);
              updateSliderBackground(slider);
            }
          }, 200);
        }, 2000);
      } else if (
        norm(nextQuestion.keywords) === "win stack" ||
        norm(nextQuestion.keywords) === "winstack"
      ) {
        setTimeout(() => {
          setIsThinkingLoading(false);
        }, 2000);
      } else {
        setTimeout(() => {
          setIsThinkingLoading(false);
        }, 2000);
      }
    } else if (allAnswered) {
      // All questions are answered, add completion message
      chatMessages.push({
        type: "send",
        question: `Thank you for completing ${reflectionType} journal entry.`,
        time: moment().format("h:mm A"),
        botImage: "/icons/journal-brave.svg",
      });
      setIsThinkingLoading(false);
    }

    // If there are any messages, we consider journaling to have begun
    if (chatMessages.length > 0) {
      setJournalingBegun(true);
    }

    setMessages(chatMessages);
    setCurrentQuestion(nextQuestion);
  };

  // Fetch journal data when reflectionType is available
  useEffect(() => {
    if (
      reflectionType &&
      previousReflectionTypeRef.current !== reflectionType
    ) {
      previousReflectionTypeRef.current = reflectionType;

      setJournalingBegun(false);
      setLoading(true);
      const token = retrieve(AUTH_TOKEN);
      const config = createApiConfig(token);

      axios
        .get(
          `${API_BASE}/reflections/type?reflection_type=${reflectionType}`,
          config,
        )
        .then((response) => {
          if (response.data?.status && response.data.data?.reflection) {
            const formattedData = {
              ...response.data.data.reflection,
              main_questions: response.data.data.reflection.main_questions.map(
                (q) => ({
                  ...q,
                  answered: q.answered || false,
                  answer: q.answer || null,
                  follow_up_questions:
                    q.follow_up_questions?.map((fq) => ({
                      ...fq,
                      answered: fq.answered || false,
                      answer: fq.answer || null,
                    })) || [],
                }),
              ),
            };

            const firstAnswered = formattedData.main_questions.find(
              (q) => q.answered && q.answer?.user_reflection_id,
            );

            if (firstAnswered) {
              setUserReflectionId(firstAnswered.answer.user_reflection_id);
            }
            setJournalData(formattedData);
            initializeChat(formattedData);
          } else {
            throw new Error("Invalid response structure");
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching reflection:", err);

          // Check if the error is due to already completed journal
          if (
            err.response &&
            err.response.data &&
            err.response.data.status === false
          ) {
            setCompletedMessage(err.response.data.message);
            setTimeout(() => {
              navigate("/reflection-library");
            }, 3000);
          } else {
            setError("Failed to fetch reflection data");
            toast.error("Failed to fetch reflection data");
          }

          setLoading(false);
        });
    }
  }, [reflectionType, navigate, userReflectionId]);

  const handleSendMessage = async (messageText) => {
    if (editingMessage) {
      await handleSaveEdit(editingMessage.messageId, messageText);
      return;
    }

    const cleanedMessage = messageText.trim();
    if (!cleanedMessage || !journalData || !currentQuestion) {
      return;
    }

    if (currentQuestion.question_type === "yes_no") return;

    // Handle score questions - extract numeric value from text
    if (currentQuestion.keywords === "score") {
      const scoreMatch = cleanedMessage.match(/\d+/);
      if (scoreMatch) {
        const score = parseInt(scoreMatch[0]);
        if (score >= 0 && score <= 10) {
          await handleScoreSubmit(score);
          return;
        }
      }
      toast.error("Please enter a score between 0 and 10");
      return;
    }

    const token = retrieve(AUTH_TOKEN);
    const currentTime = moment().format("h:mm A");

    // Add user's message to chat immediately
    const newUserMessage = {
      type: "receive",
      response: cleanedMessage,
      time: currentTime,
      responder: userData?.full_name || "User",
      userImage: userData?.image || "/icons/toni.svg",
    };
    setMessages((prev) => [...prev, newUserMessage]);

    // Prepare payload
    const payload = {
      reflection_id: journalData.id,
      text: cleanedMessage,
      ...(currentQuestion.isFollowUp
        ? { follow_up_question_id: currentQuestion.id }
        : { main_question_id: currentQuestion.id }),
      ...(userReflectionId && { user_reflection_id: userReflectionId }),
    };

    try {
      const config = createApiConfig(token, false); // Don't include in params since it's in payload

      // Submit answer
      const answerResponse = await axios.post(
        `${API_BASE}/reflection-answers`,
        payload,
        config,
      );

      // Update userReflectionId from response
      const updatedUserReflectionId =
        answerResponse.data.data.user_reflection_id;

      setUserReflectionId(updatedUserReflectionId);

      try {
        // Fetch updated reflection data with the updated userReflectionId
        const reflectionConfig = createApiConfig(token);
        // Use the fresh userReflectionId from API response
        if (updatedUserReflectionId) {
          reflectionConfig.params = {
            user_reflection_id: updatedUserReflectionId,
          };
        }

        const reflectionResponse = await axios.get(
          `${API_BASE}/reflections/type?reflection_type=${reflectionType}`,
          reflectionConfig,
        );

        const updatedReflection = {
          ...reflectionResponse.data.data.reflection,
          main_questions:
            reflectionResponse.data.data.reflection.main_questions.map((q) => ({
              ...q,
              answered: q.answered || false,
              answer: q.answer || null,
              follow_up_questions:
                q.follow_up_questions?.map((fq) => ({
                  ...fq,
                  answered: fq.answered || false,
                  answer: fq.answer || null,
                })) || [],
            })),
        };

        setJournalData(updatedReflection);

        const allMainAnswered = updatedReflection.main_questions.every(
          (q) => q.answered,
        );
        const allFollowUpAnswered = updatedReflection.main_questions.every(
          (q) =>
            !q.follow_up_questions ||
            q.follow_up_questions.every((fq) => fq.answered),
        );

        if (allMainAnswered && allFollowUpAnswered) {
          handleJournalCompletion();
        } else {
          initializeChat(updatedReflection);
        }
      } catch (fetchErr) {
        // Fallback: Handle with local data
        const updatedLocalJournal = { ...journalData };
        const questionId = currentQuestion.id;
        const isFollowUp = currentQuestion.isFollowUp;

        if (isFollowUp) {
          for (const mainQ of updatedLocalJournal.main_questions) {
            const index = mainQ.follow_up_questions?.findIndex(
              (fq) => fq.id === questionId,
            );
            if (index !== -1) {
              mainQ.follow_up_questions[index].answered = true;
              mainQ.follow_up_questions[index].answer = {
                id: Date.now(),
                text: cleanedMessage,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              };
            }
          }
        } else {
          const index = updatedLocalJournal.main_questions.findIndex(
            (q) => q.id === questionId,
          );
          if (index !== -1) {
            updatedLocalJournal.main_questions[index].answered = true;
            updatedLocalJournal.main_questions[index].answer = {
              id: Date.now(),
              text: cleanedMessage,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
          }
        }

        const allMainAnswered = updatedLocalJournal.main_questions.every(
          (q) => q.answered,
        );
        const allFollowUpAnswered = updatedLocalJournal.main_questions.every(
          (q) =>
            !q.follow_up_questions ||
            q.follow_up_questions.every((fq) => fq.answered),
        );

        setJournalData(updatedLocalJournal);

        if (allMainAnswered && allFollowUpAnswered) {
          handleJournalCompletion();
        } else {
          initializeChat(updatedLocalJournal);
        }
      }
    } catch (error) {
      const updatedLocalJournal = { ...journalData };
      const questionId = currentQuestion.id;
      const isFollowUp = currentQuestion.isFollowUp;

      if (isFollowUp) {
        for (const mainQ of updatedLocalJournal.main_questions) {
          const index = mainQ.follow_up_questions?.findIndex(
            (fq) => fq.id === questionId,
          );
          if (index !== -1) {
            mainQ.follow_up_questions[index].answered = true;
            mainQ.follow_up_questions[index].answer = {
              id: Date.now(),
              text: cleanedMessage,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
          }
        }
      } else {
        const index = updatedLocalJournal.main_questions.findIndex(
          (q) => q.id === questionId,
        );
        if (index !== -1) {
          updatedLocalJournal.main_questions[index].answered = true;
          updatedLocalJournal.main_questions[index].answer = {
            id: Date.now(),
            text: cleanedMessage,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
        }
      }

      const allMainAnswered = updatedLocalJournal.main_questions.every(
        (q) => q.answered,
      );
      const allFollowUpAnswered = updatedLocalJournal.main_questions.every(
        (q) =>
          !q.follow_up_questions ||
          q.follow_up_questions.every((fq) => fq.answered),
      );

      setJournalData(updatedLocalJournal);

      if (allMainAnswered && allFollowUpAnswered) {
        handleJournalCompletion();
      } else {
        initializeChat(updatedLocalJournal);
      }
    }
  };

  // Handle score question submission
  const handleScoreSubmit = async (score) => {
    if (!journalData || !currentQuestion) {
      return;
    }

    const token = retrieve(AUTH_TOKEN);
    const currentTime = moment().format("h:mm A");

    // Add user's score response to chat immediately
    const newUserMessage = {
      type: "receive",
      response: `Score: ${score}`,
      time: currentTime,
      responder: userData?.full_name || "User",
      userImage: userData?.image || "/icons/toni.svg",
    };
    setMessages((prev) => [...prev, newUserMessage]);

    // Prepare payload
    const payload = {
      reflection_id: journalData.id,
      text: score.toString(),
      ...(currentQuestion.isFollowUp
        ? { follow_up_question_id: currentQuestion.id }
        : { main_question_id: currentQuestion.id }),
      ...(userReflectionId && { user_reflection_id: userReflectionId }),
    };

    try {
      const config = createApiConfig(token, false);
      const answerResponse = await axios.post(
        `${API_BASE}/reflection-answers`,
        payload,
        config,
      );

      const updatedUserReflectionId =
        answerResponse.data.data.user_reflection_id;
      setUserReflectionId(updatedUserReflectionId);

      // Fetch updated reflection data and continue flow
      await handleAnswerSubmissionSuccess(updatedUserReflectionId);
      setShowScoreSlider(false); // Hide slider after successful submission
    } catch (error) {
      toast.error("Failed to submit score. Please try again.");
    }
  };

  // Handle win stack question submission
  const handleWinStackSubmit = async (selectedTasks) => {
    if (!journalData || !currentQuestion || selectedTasks.length === 0) {
      return;
    }

    const token = retrieve(AUTH_TOKEN);
    const currentTime = moment().format("h:mm A");

    // Create task list text
    const taskListText = selectedTasks
      .map((task) => `• ${task.title}`)
      .join("\n");

    // Add user's task selection to chat immediately
    const newUserMessage = {
      type: "receive",
      response: `Selected ${selectedTasks.length} tasks:\n${taskListText}`,
      time: currentTime,
      responder: userData?.full_name || "User",
      userImage: userData?.image || "/icons/toni.svg",
    };
    setMessages((prev) => [...prev, newUserMessage]);

    // Prepare payload
    const payload = {
      reflection_id: journalData.id,
      text: taskListText,
      ...(currentQuestion.isFollowUp
        ? { follow_up_question_id: currentQuestion.id }
        : { main_question_id: currentQuestion.id }),
      ...(userReflectionId && { user_reflection_id: userReflectionId }),
    };

    try {
      const config = createApiConfig(token, false);
      const answerResponse = await axios.post(
        `${API_BASE}/reflection-answers`,
        payload,
        config,
      );

      const updatedUserReflectionId =
        answerResponse.data.data.user_reflection_id;
      setUserReflectionId(updatedUserReflectionId);

      // Clear selected tasks
      setSelectedTasks([]);

      // Fetch updated reflection data and continue flow
      await handleAnswerSubmissionSuccess(updatedUserReflectionId);
    } catch (error) {
      toast.error("Failed to submit task selection. Please try again.");
    }
  };

  // Toggle task selection for win stack questions
  const toggleTaskSelection = (task) => {
    setSelectedTasks((prev) => {
      const isSelected = prev.some((t) => t.id === task.id);
      if (isSelected) {
        return prev.filter((t) => t.id !== task.id);
      } else {
        return [...prev, task];
      }
    });
  };

  // Helper function to handle successful answer submission
  const handleAnswerSubmissionSuccess = async (updatedUserReflectionId) => {
    try {
      const token = retrieve(AUTH_TOKEN);
      const reflectionConfig = createApiConfig(token);

      if (updatedUserReflectionId) {
        reflectionConfig.params = {
          user_reflection_id: updatedUserReflectionId,
        };
      }

      const reflectionResponse = await axios.get(
        `${API_BASE}/reflections/type?reflection_type=${reflectionType}`,
        reflectionConfig,
      );

      const updatedReflection = {
        ...reflectionResponse.data.data.reflection,
        main_questions:
          reflectionResponse.data.data.reflection.main_questions.map((q) => ({
            ...q,
            answered: q.answered || false,
            answer: q.answer || null,
            follow_up_questions:
              q.follow_up_questions?.map((fq) => ({
                ...fq,
                answered: fq.answered || false,
                answer: fq.answer || null,
              })) || [],
          })),
      };

      setJournalData(updatedReflection);

      const allMainAnswered = updatedReflection.main_questions.every(
        (q) => q.answered,
      );
      const allFollowUpAnswered = updatedReflection.main_questions.every(
        (q) =>
          !q.follow_up_questions ||
          q.follow_up_questions.every((fq) => fq.answered),
      );

      if (allMainAnswered && allFollowUpAnswered) {
        handleJournalCompletion();
      } else {
        initializeChat(updatedReflection);
      }
    } catch (fetchErr) {
      toast.error("Failed to load next question. Please refresh the page.");
    }
  };

  // edit message
  // Add these handler functions
  const handleStartEdit = (answerId, currentText) => {
    if (!answerId) {
      toast.error("Cannot edit this message - missing ID");
      return;
    }

    // Find the message being edited and its index
    const messageIndex = messages.findIndex(
      (msg) => msg.type === "receive" && msg.answerId === answerId,
    );
    const messageToEdit = messages[messageIndex];

    // Check if it's a yes/no answer
    if (messageToEdit?.questionType === "yes_no") {
      toast.error("Cannot edit yes/no answers");
      return;
    }

    // Check if it's a score question - if so, show slider instead of text box
    if (
      messageToEdit?.questionType === "score" ||
      currentText.match(/^Score: \d+$/)
    ) {
      const scoreValue = parseInt(currentText.replace("Score: ", "")) || 5;
      setScoreValues((prev) => ({ ...prev, [messageIndex - 1]: scoreValue }));
      setShowScoreSlider(true);
      setEditingQuestionIndex(messageIndex - 1); // The question is the previous message
      setEditingMessage({ messageId: answerId, currentText, isScore: true });
      setEditingScoreIndex(messageIndex - 1); // Track which score question is being edited
    } else {
      setEditingMessage({ messageId: answerId, currentText });
    }
  };

  const handleCancelEdit = () => {
    if (editingMessage?.isScore) {
      setShowScoreSlider(false);
      setEditingScoreIndex(null);
    }
    setEditingMessage(null);
    setEditingQuestionIndex(null);
  };

  const handleSaveEdit = async (answerId, newText) => {
    if (!answerId) {
      toast.error("Cannot save edit - missing message ID");
      return;
    }

    const token = retrieve(AUTH_TOKEN);

    try {
      let cleanedText;

      // If it's a score edit, use the slider value instead of text
      if (editingMessage?.isScore) {
        // Check if newText contains a score value (from message box)
        const scoreMatch = newText.match(/\d+/);
        if (scoreMatch) {
          const score = parseInt(scoreMatch[0]);
          if (score >= 0 && score <= 10) {
            cleanedText = score.toString();
          } else {
            toast.error("Please enter a score between 0 and 10");
            return;
          }
        } else {
          cleanedText = scoreValues.toString(); // Use slider value
        }
        setShowScoreSlider(false);
        setEditingQuestionIndex(null);
      } else {
        cleanedText = newText.trim();
      }
      if (editingMessage?.isScore) {
        setEditingScoreIndex(null);
        setScoreValues((prev) => {
          const copy = { ...prev };
          if (editingQuestionIndex !== null) delete copy[editingQuestionIndex];
          return copy;
        });
      }

      const response = await axios.post(
        `${API_BASE}/reflection-answers/${answerId}`,
        { text: cleanedText },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      // Refresh data
      const reflectionConfig = createApiConfig(token);
      const reflectionResponse = await axios.get(
        `${API_BASE}/reflections/type?reflection_type=${reflectionType}`,
        reflectionConfig,
      );

      const updatedReflection = {
        ...reflectionResponse.data.data.reflection,
        main_questions:
          reflectionResponse.data.data.reflection.main_questions.map((q) => ({
            ...q,
            answered: q.answered || false,
            answer: q.answer || null,
            follow_up_questions:
              q.follow_up_questions?.map((fq) => ({
                ...fq,
                answered: fq.answered || false,
                answer: fq.answer || null,
              })) || [],
          })),
      };

      setJournalData(updatedReflection);
      const allMainAnswered = updatedReflection.main_questions.every(
        (q) => q.answered,
      );
      const allFollowUpAnswered = updatedReflection.main_questions.every(
        (q) =>
          !q.follow_up_questions ||
          q.follow_up_questions.every((fq) => fq.answered),
      );

      if (allMainAnswered && allFollowUpAnswered) {
        handleJournalCompletion();
      } else {
        initializeChat(updatedReflection);
      }
      setEditingMessage(null);
      toast.success("Answer updated successfully");
    } catch (err) {
      toast.error("Failed to update answer");

      if (err.response?.status === 404) {
        toast.error("The message you're trying to edit no longer exists");
      }
    }
  };

  useEffect(() => {
    if (!allQuestionsAnswered) {
      if (messages.length) {
        store.dispatch(setAlert("jounral_ongoing"));
      }
    } else {
      store.dispatch(resetAlert());
    }
  }, [messages, allQuestionsAnswered]);

  const handleBeginJournaling = () => {
    setJournalingBegun(true);
    if (journalData?.main_questions?.length > 1) {
      initializeChat(journalData);
    }
  };

  const handleYesNoResponse = async (response) => {
    const messageText = response ? "yes" : "no";
    const currentTime = moment().format("h:mm A");

    // Check if we're editing an existing answer
    const isEditing = editingMessage?.isYesNo;
    const answerId = isEditing ? editingMessage.messageId : Date.now();

    // Create the new user message
    const newUserMessage = {
      type: "receive",
      response: messageText,
      time: currentTime,
      responder: userData?.full_name || "User",
      userImage: userData?.image || "/icons/toni.svg",
      answerId,
      questionType: currentYesNoQuestion.question_type,
    };

    try {
      // First update the UI immediately
      setMessages((prev) => {
        if (isEditing) {
          return prev.map((msg) =>
            msg.type === "receive" && msg.answerId === answerId
              ? newUserMessage
              : msg,
          );
        } else {
          return [...prev, newUserMessage];
        }
      });

      setShowYesNoModal(false);
      if (isEditing) setEditingMessage(null);

      // Prepare API payload
      const token = retrieve(AUTH_TOKEN);
      const payload = {
        reflection_id: journalData.id,
        text: messageText,
        ...(userReflectionId && { user_reflection_id: userReflectionId }),
      };

      if (currentYesNoQuestion.isFollowUp) {
        payload.follow_up_question_id = currentYesNoQuestion.id;
      } else {
        payload.main_question_id = currentYesNoQuestion.id;
      }

      // Submit the answer to the API
      let answerResponse;
      if (isEditing) {
        answerResponse = await axios.post(
          `${API_BASE}/reflection-answers/${answerId}`,
          { text: messageText },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
      } else {
        const config = createApiConfig(token, false);
        // Don't include in params since it's in payload
        answerResponse = await axios.post(
          `${API_BASE}/reflection-answers`,
          payload,
          config,
        );

        // Update userReflectionId from response
        const updatedUserReflectionId =
          answerResponse.data.data.user_reflection_id;
        setUserReflectionId(updatedUserReflectionId);
      }

      // Only show success toast when editing
      if (isEditing) {
        toast.success("Answer updated successfully");
      }

      // Update the message with the real answer ID if it was a new answer
      if (!isEditing) {
        const realAnswerId = answerResponse.data.data.id;
        setMessages((prev) => {
          const updated = [...prev];
          const lastMessage = updated[updated.length - 1];
          if (lastMessage && lastMessage.type === "receive") {
            updated[updated.length - 1] = {
              ...lastMessage,
              answerId: realAnswerId,
            };
          }
          return updated;
        });
      }

      // Fetch updated reflection data to get the complete state
      const reflectionConfig = createApiConfig(token);
      const reflectionResponse = await axios.get(
        `${API_BASE}/reflections/type?reflection_type=${reflectionType}`,
        reflectionConfig,
      );

      const updatedReflection = {
        ...reflectionResponse.data.data.reflection,
        main_questions:
          reflectionResponse.data.data.reflection.main_questions.map((q) => ({
            ...q,
            answered: q.answered || false,
            answer: q.answer || null,
            follow_up_questions:
              q.follow_up_questions?.map((fq) => ({
                ...fq,
                answered: fq.answered || false,
                answer: fq.answer || null,
              })) || [],
          })),
      };

      setJournalData(updatedReflection);

      const allMainAnswered = updatedReflection.main_questions.every(
        (q) => q.answered,
      );
      const allFollowUpAnswered = updatedReflection.main_questions.every(
        (q) =>
          !q.follow_up_questions ||
          q.follow_up_questions.every((fq) => fq.answered),
      );

      if (allMainAnswered && allFollowUpAnswered) {
        handleJournalCompletion();
      } else {
        initializeChat(updatedReflection);
      }
    } catch (err) {
      toast.error("Error submitting yes/no answer.");

      // Revert the message if editing
      setMessages((prev) => {
        if (isEditing) {
          return prev.map((msg) =>
            msg.type === "receive" && msg.answerId === answerId
              ? { ...msg, response: editingMessage.currentText }
              : msg,
          );
        } else {
          return prev.slice(0, -1);
        }
      });

      // Show error and retry
      setMessages((prev) => [
        ...prev,
        {
          type: "send",
          question: "Sorry, something went wrong. Please try again.",
          time: moment().format("h:mm A"),
          botImage: "/icons/journal-brave.svg",
        },
        {
          type: "send",
          question: currentYesNoQuestion.question,
          time: moment().format("h:mm A"),
          botImage: "/icons/journal-brave.svg",
        },
      ]);

      setShowYesNoModal(true);
    }
  };

  //yes no
  const handleShowYesNoModal = (answerId, currentText) => {
    // Find the question associated with this answer
    let questionToEdit = null;

    // Search through main questions
    for (const mainQ of journalData.main_questions) {
      if (mainQ.answer?.id === answerId) {
        questionToEdit = { ...mainQ, isFollowUp: false };
        break;
      }

      // Search through follow-up questions
      if (mainQ.follow_up_questions) {
        for (const followUpQ of mainQ.follow_up_questions) {
          if (followUpQ.answer?.id === answerId) {
            questionToEdit = {
              ...followUpQ,
              isFollowUp: true,
              mainQuestionId: mainQ.id,
            };
            break;
          }
        }
      }
      if (questionToEdit) break;
    }

    if (questionToEdit) {
      setCurrentYesNoQuestion(questionToEdit);
      setShowYesNoModal(true);
      setEditingMessage({
        messageId: answerId,
        currentText,
        isYesNo: true,
      });
    } else {
      toast.error("Could not find the question to edit");
    }
  };

  // Validation and rendering
  if (!["am", "pm"].includes(reflectionType)) {
    return <div>Invalid journal type.</div>;
  }

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!journalData) return <div>No journal data found.</div>;

  return (
    <Layout>
      <div className="page-content">
        <div className="journal-chat-content">
          <div className="d-flex justify-content-between flex-column flex-sm-row">
            <h1 className="pages-header text-heading-h2">
              {reflectionType.toUpperCase()}{" "}
              {reflectionType == "am" ? "Journal" : "Reflection"}
            </h1>
            <div className="goto-btn">
              <button
                className="btn-1 btn-1-primary text-body-b3"
                onClick={() => navigate("/reflection-library")}
                style={{
                  maxWidth: "205px",
                  width: "100%",
                  maxHeight: "39px",
                  height: "100%",
                  whiteSpace: "nowrap",
                }}
              >
                Go to Reflection Library
              </button>
            </div>
          </div>
          <div className="d-flex flex-column goals-chat-card-container">
            {loading && (
              <div className="loader-container">
                <div className="loader"></div>
              </div>
            )}
            {error && <p>{error}</p>}

            <div
              ref={chatContainerRef}
              className="d-flex flex-column no-scrollbar"
              style={{
                flexGrow: 1,
                overflowX: "hidden",
                minHeight: 0,
                height: 0,
                flexShrink: 0,
              }}
            >
              {/* Render chat messages */}
              {messages.map((message, index) =>
                message.type === "send" ? (
                  <ChatSend
                    key={`send-${index}`}
                    username="Empowered Man"
                    title={message.question}
                    time={message.time}
                    botImage="/icons/journal-brave.svg"
                    showTaskList={Boolean(message.showTaskList)}
                    taskLists={
                      message.taskLists ||
                      (message.showTaskList ? buildTaskLists() : null)
                    }
                    isWinStack={Boolean(message.showTaskList)}
                    achievedTasks={doneAchievedTasks?.achieved_tasks || []}
                    doneTasks={doneAchievedTasks?.done_tasks || []}
                    notDoneTasks={doneAchievedTasks?.not_done || []}
                    setIsThinkingLoading={setIsThinkingLoading}
                    isThinkingLoading={isThinkingLoading}
                    isCurrentQuestion={
                      index === messages.length - 1 && message.type === "send"
                    }
                    isScoreQuestion={Boolean(
                      currentQuestion?.keywords === "score" &&
                      index === messages.length - 1,
                    )}
                    sliderValue={scoreValues[index] ?? 0}
                    setSliderValue={(val) =>
                      setScoreValues((prev) => ({ ...prev, [index]: val }))
                    }
                    scoreValue={scoreValues[index] ?? 0}
                    setScoreValue={(val) =>
                      setScoreValues((prev) => ({ ...prev, [index]: val }))
                    }
                    sliderRef={sliderRef}
                    updateSliderBackground={updateSliderBackground}
                    handleScoreSubmit={handleScoreSubmit}
                    isCompleted={isCompleted}
                    editingMessage={editingMessage}
                    handleSaveEdit={handleSaveEdit}
                    messageIndex={index}
                    editingQuestionIndex={editingQuestionIndex}
                    answeredValue={
                      typeof message.answeredValue === "number"
                        ? message.answeredValue
                        : null
                    }
                  />
                ) : (
                  <ChatReceive
                    key={`receive-${index}`}
                    response={message.response}
                    responder={message.responder}
                    time={message.time}
                    userImage={message.userImage}
                    answerId={message.answerId}
                    questionType={message.questionType}
                    isEditing={editingMessage?.messageId === message.answerId}
                    onEdit={handleStartEdit}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                    disableEdit={message.questionType === "yes_no"}
                    onShowYesNoModal={handleShowYesNoModal}
                  />
                ),
              )}

              {/* Show begin button if no messages yet */}
              {messages.length === 1 &&
                !loading &&
                !error &&
                !journalingBegun && (
                  <button
                    className="text-body-b3 btn-1 btn-1-primary"
                    id="journal-button"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "240px",
                      height: "52px",
                      cursor: "pointer",
                      alignSelf: "center",
                      flexShrink: 0,
                    }}
                    onClick={handleBeginJournaling}
                  >
                    Begin Journaling
                  </button>
                )}
            </div>

            {/* Message Box - show for all questions except yes/no, but disable for score questions */}

            {!showYesNoModal &&
            ((currentQuestion &&
              !allQuestionsAnswered &&
              journalingBegun &&
              currentQuestion.question_type !== "yes_no" &&
              !editingMessage) ||
              (editingMessage &&
                !editingMessage.isYesNo &&
                !editingMessage.isScore)) ? (
              <div>
                <MessageBox
                  onSendClick={handleSendMessage}
                  initialText={editingMessage?.currentText || ""}
                  isEditing={!!editingMessage}
                  setEditingMessage={setEditingMessage}
                  userProfile={userData}
                  draftScopeId={reflectionComposerDraftScopeId}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Yes/No Question Modal */}
      {showYesNoModal && currentYesNoQuestion && !isThinkingLoading && (
        <div
          className="modal fade not-completed-journal-modal show"
          id="notCompletedJournalModal"
          tabIndex="-1"
          aria-labelledby="notCompletedJournalModalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header position-relative">
                <h1
                  className="modal-title"
                  id="notCompletedJournalModalLabel"
                ></h1>
              </div>

              <div className="modal-body">
                <p
                  className="text-body-b2 mb-4 text-wrap"
                  style={{ fontSize: "19px" }}
                >
                  {currentYesNoQuestion.question}
                </p>
                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px", height: "auto" }}
                    onClick={() => handleYesNoResponse(true)}
                  >
                    Yes
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px", height: "auto" }}
                    onClick={() => handleYesNoResponse(false)}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div
          className="modal fade not-completed-journal-modal show"
          id="notCompletedJournalModal"
          tabIndex="-1"
          aria-labelledby="notCompletedJournalModalLabel"
          aria-hidden="true"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header position-relative">
                <button
                  type="button"
                  className="btn-close btn-close-white position-absolute"
                  style={{ top: "14px", right: "0px", color: "#e6ecf1" }}
                  aria-label="Close"
                  onClick={cancel}
                ></button>
                <h1 className="modal-title" id="notCompletedJournalModalLabel">
                  You have not completed
                  <br />
                  this journal
                </h1>
              </div>

              <div className="modal-body">
                <p className="text-body-b2 mb-4">
                  Do you want to leave this page?
                </p>
                <p className="text-body-b2 mb-0">
                  You can continue this journal from <br />
                  dashboard or reflection library
                </p>
                <div
                  className="d-flex gap-3 justify-content-center"
                  style={{ marginTop: "32px" }}
                >
                  <button
                    className="btn-1 btn-1-primary px-3 text-body-b1"
                    style={{ width: "182px", background: "#db4545" }}
                    onClick={() => {
                      store.dispatch(resetAlert());
                      proceed();
                    }}
                  >
                    Yes
                  </button>
                  <button
                    className="btn-1 btn-1-basic px-3 text-body-b1"
                    style={{ width: "166px" }}
                    onClick={cancel}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
